import { inject, Injectable } from '@angular/core';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';

import { NotificationsService } from 'app/services/notifications';

type ErrorMessage = {
    message: string;
    code: number;
};

// Errors reaching this service come from many disparate sources (GraphQL, HTTP,
// plain strings, validation payloads) with no single shared shape - ErrorLike
// captures the superset of fields actually read below.
type ErrorLike = {
    errors?: unknown;
    error?: unknown;
    validation?: Record<string, string[]>;
    message?: string;
    code?: number;
};

const TYPE_ERROR_PATTERNS = [
    /Unknown type "\S+"\. Did you mean ("\S+"(, )?)+( or "\S+")?\?/,
    /Variable "\S+" of type "\S+" used in position expecting type "\S+"\./,
    /Unknown argument "\S+" on field "\S+" of type "\S+"/,
];

@Injectable()
export class ErrorService {
    private notificationsService = inject(NotificationsService);

    private returnValidationErrors(errors: unknown, errorMessages: ErrorMessage[]): void {
        if (!errors) {
            return;
        }

        const errorLike = errors as ErrorLike;
        let errorsArray: ErrorLike[];
        if (Array.isArray(errorLike?.errors)) {
            errorsArray = errorLike.errors;
        } else if (Array.isArray(errors)) {
            errorsArray = errors;
        } else {
            errorsArray = [errorLike];
        }

        errorsArray.forEach((error) => {
            if (error?.validation) {
                for (const key in error.validation) {
                    if (error.validation.hasOwnProperty(key)) {
                        error.validation[key].forEach((validationError: string) => {
                            errorMessages.push({ message: validationError, code: 400 });
                        });
                    }
                }
            } else if (error?.message) {
                errorMessages.push({ message: error.message, code: error.code || 400 });
            } else if (typeof error === 'string') {
                errorMessages.push({ message: error, code: 400 });
            }
        });
    }

    private formatAndReturnErrors(errors: unknown, name = 'error.unknown'): ErrorMessage[] {
        const errorMessages: ErrorMessage[] = [];

        const isCombinedGraphQLErrors = CombinedGraphQLErrors.is(errors);
        const isServerError = ServerError.is(errors);

        if (isServerError) {
            this.returnValidationErrors(this.parseServerErrorBody(errors), errorMessages);
        }

        const processedErrors = isCombinedGraphQLErrors ? errors.errors : errors;

        if (typeof processedErrors !== 'string' && (processedErrors as ErrorLike[])?.length) {
            this.returnValidationErrors(processedErrors, errorMessages);
        }

        // Handle plain HttpErrorResponse — body is at errors.error
        const errorLike = errors as ErrorLike;
        if (!errorMessages.length && errorLike?.error && !isCombinedGraphQLErrors && !isServerError) {
            this.returnValidationErrors(errorLike.error, errorMessages);
        }

        if (!errorMessages.length) {
            errorMessages.push({ message: name, code: 500 });
        }

        const hasTypeMismatch = errorMessages.some((error) => this.identifyTypeErrorFromRegex(error.message));

        if (hasTypeMismatch) {
            this.notificationsService.translateTypeMismatchModal();
        }

        return errorMessages;
    }

    private parseServerErrorBody(error: ServerError): unknown {
        try {
            return JSON.parse(error.bodyText)?.errors;
        } catch {
            return undefined;
        }
    }

    formatErrors(errors: unknown, name = 'error.unknown'): string[] {
        return this.formatAndReturnErrors(errors, name).map((error) => error.message);
    }

    handleError(errors: unknown, name?: string, useErrorCode = true, id?: string): void {
        const errorMessages = this.formatAndReturnErrors(errors, name);

        errorMessages.forEach((error) => {
            const notificationId = useErrorCode ? `${id || 'error'}-${error.code}` : id;
            if (error.message === 'error.unknown' || error.message === name) {
                this.notificationsService.translateError(error.message, null, notificationId);
            } else {
                this.notificationsService.error(error.message, null, notificationId);
            }
        });
    }

    identifyTypeErrorFromRegex(errorMsg: string): boolean {
        if (!errorMsg) return false;
        return TYPE_ERROR_PATTERNS.some((re) => re.test(errorMsg));
    }
}
